import { Box, Grid, IconButton, Link, Stack, TextField, Tooltip, Typography } from '@mui/material';
import React, { useRef, useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { postAddTicket } from '../../../../api/tickets';
import { useTabs } from '../../../../context/TabContext';
import { editTicketData } from '../../../../redux/slices/tickets';
import { useDispatch, useSelector } from '../../../../redux/store';
import { getRentalOrderStatus } from '../../../../api/rentals';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import EditSubject from './EditSubject';
import AttachOrderToTicket from './AttachOrderToTicket';

export default function TicketSubject() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { addNewTabAndNavigate } = useTabs();
  const { id = '' } = useParams();
  const { subtickets, mainTicket } = useSelector((state) => state.tickets.data.ticketInfoById[id]) || {};
  const [searchParams] = useSearchParams();
  const start = searchParams.get('start') || new Date(new Date().setDate(new Date().getDate() - 29)).toISOString();
  const end = searchParams.get('end') || new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString();

  const subticketId = new URLSearchParams(location.search).get('subticket');
  const { ticketNumber } = useSelector((state) => state.tickets.data.ticketById[subticketId || id]) || {};

  const [ticketNum, setTicketNum] = useState(false);
  const [editSubject, setEditSubject] = useState(false);
  const [addingSubtcket, setAddingSubtcket] = useState(false);
  const [subject, setSubject] = useState(mainTicket?.extraInfo || '');
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [ticketRentalStatus, setTicketRentalStatus] = useState(null);

  // For tooltip on subject overflow
  const subjectRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const showTooltipCheck = () => {
    if (subjectRef.current) {
      setShowTooltip(subjectRef.current.scrollWidth > subjectRef.current.clientWidth);
    }
  };

  useEffect(() => {
    showTooltipCheck();
  }, [editSubject]);

  useEffect(() => {
    if(mainTicket?.ticketRentalId > 0) {
      getRentalOrderStatus(mainTicket.ticketRentalId).then((response) => {
        setTicketRentalStatus(response.data);
      })
      .catch(() => enqueueSnackbar('Failed to get rental order status', { variant: 'error' }))
    }
  }, [mainTicket?.ticketRentalId])

  useEffect(() => {
    if (!editSubject && subject) showTooltipCheck();
  }, [subject]);

  const checkTicketId = (ticketId) => {
    if (subticketId === null) {
      if (id === ticketId) return true;
      return false;
    }
    if (parseInt(subticketId, 10) === ticketId) return true;
    if (subticketId === ticketId) return true;
    return false;
  };

  useEffect(() => {
    if (!subtickets) setTicketNum(id);
    if (subticketId === null) {
      setTicketNum(id);
    } else {
      setTicketNum(subtickets?.find((subticket) => subticket.id === parseInt(subticketId, 10))?.ticketName);
    }
  }, [subtickets, id, subticketId]);

  useEffect(() => {
    setSubject(
      (subticketId ? subtickets?.find((subticket) => subticket.id === parseInt(subticketId, 10))?.extraInfo : null) ??
        mainTicket?.extraInfo
    );
  }, [mainTicket, subtickets, subticketId]);

  const editTicket = async ({ Id, value, updateType }) => {
    const response = await dispatch(editTicketData({ Id: ticketNumber || Id, value, updateType, mainId: id }));
    if (response) {
      enqueueSnackbar(`Ticket updated successfully`, { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to update ticket', { variant: 'error' });
    }
  };

  const handleEditSubject = () => {
    editTicket({ Id: ticketNumber || id, value: subject, updateType: 'ExtraInfo' });
  };
  const handleCancelSubject = () => {
    setEditSubject(false);
    setSubject(mainTicket?.extraInfo || '');
  };

  const handleSend = async () => {
    setAddingSubtcket(true);
    postAddTicket({ message: '', parentTicketId: id, subject: mainTicket?.extraInfo ?? '' })
      .then((response) => {
        if (response.data.id)
          navigate(`/dashboard/tickets/${id}?subticket=${response.data.id}&start=${start}&end=${end}`);
      })
      .catch(() => enqueueSnackbar('Failed to add subticket', { variant: 'error' }))
      .finally(() => setAddingSubtcket(false));
  };

  return (
    <>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="start"
        wrap="nowrap"
        sx={{ mt: 3, mb: 0.5, width: '100%' }}
        columnSpacing={2}
      >
        <Grid item sx={{ minWidth: 0, flexGrow: 1, flexShrink: 1 }}>
          <Box>
            <Typography variant="subtitle1" color="info.main" sx={{ fontWeight: 'bold' }}>
              Ticket #{ticketNum}
            </Typography>
          </Box>
          {editSubject ? (
            <Box sx={{ display: 'flex' }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ alignContent: 'center', fontWeight: 'normal' }}
              >
                Subject:
              </Typography>
              <TextField
                id="standard-search"
                variant="standard"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                }}
                autoComplete="off"
                sx={{
                  height: '30px',
                  width: '100%',
                  mb: 1,
                  '& .MuiInputBase-input': {
                    height: '30px',
                    fontSize: '16px',
                    fontWeight: '400',
                    margin: 0,
                    marginLeft: 0.5,
                    marginBottom: -1,
                    fontFamily: `Public Sans,sans-serif`,
                    '& .MuiInputBase-input:focus': {
                      height: '30px',
                    },
                  },
                }}
              />
              <IconButton
                onClick={() => {
                  handleCancelSubject();
                }}
                color="warning"
              >
                <Iconify icon="iconoir:cancel" />
              </IconButton>
              <IconButton
                onClick={() => {
                  setEditSubject(false);
                  handleEditSubject(subject);
                }}
                color="primary"
              >
                <Iconify icon="material-symbols:check" />
              </IconButton>
            </Box>
          ) : (
            <Tooltip title={showTooltip ? subject : ''} disableHoverListener={!showTooltip} placement="top">
              <Box sx={{ display: 'flex' }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    alignContent: 'center',
                    fontWeight: 'normal',
                  }}
                  ref={subjectRef}
                >
                  Subject: {subject}
                </Typography>
                {!mainTicket?.locked && (
                  <IconButton onClick={() => setEditSubject(true)} sx={{ ml: 0 }} color="primary">
                    <Iconify icon="material-symbols:edit" width={15} height={5} sx={{ mr: 0.5 }} />
                  </IconButton>
                )}
              </Box>
            </Tooltip>
          )}
        </Grid>
        <Grid item xs="auto" sx={{ flexShrink: 0 }}>
          <Grid container direction="row" justifyContent="flex-end" alignItems="center">
            <Grid item sx={{ mb: 1, justifyItems: 'end' }} xs>
              {(mainTicket?.ticketOrderId > 0 || mainTicket?.ticketRentalId > 0) && !isEditingOrder ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ fontWeight: 'normal', mr: 1, display: 'inline' }}
                  >
                    Order Number:
                  </Typography>
                  <Link color="info.main" sx={{ textDecoration: 'underline', display: 'inline' }}>
                    <Box
                      onClick={() =>
                        mainTicket?.ticketOrderId > 0
                          ? addNewTabAndNavigate(`/dashboard/orders/${mainTicket?.ticketOrderId}`)
                          : mainTicket?.ticketRentalId > 0
                          ? addNewTabAndNavigate(`/dashboard/rentals/rental-order/${mainTicket?.ticketRentalId}/${ticketRentalStatus}`)
                          : null
                      }
                      sx={{ display: 'inline', cursor: 'pointer' }}
                    >
                      {mainTicket?.ticketOrderId || mainTicket?.ticketRentalId}
                    </Box>
                  </Link>
                  {!isEditingOrder && (
                    <IconButton onClick={() => setIsEditingOrder(true)}>
                      <Iconify icon="mdi:pencil" width={16} height={16} color="primary" />
                    </IconButton>
                  )}
                </Box>
              ) : !mainTicket?.locked ? (
                <AttachOrderToTicket setIsEditingOrder={setIsEditingOrder} />
              ) : null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container direction="row" justifyContent="flex-start" alignItems="center" sx={{}}>
        <Stack direction="row" maxWidth="calc(100% - 100px)" sx={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
          <Link
            to={`/dashboard/tickets/${id}`}
            component={RouterLink}
            sx={{
              padding: 1,
              backgroundColor: checkTicketId(id) ? 'info.main' : 'transparent',
              borderRadius: '3px',
              border: `1px solid #8BC5EF`,
              borderBottom: 'none',
              color: checkTicketId(id) ? '#fff' : 'text.secondary',
              ':hover': { backgroundColor: 'info.main', color: '#fff !important' },
              fontWeight: 'normal',
            }}
            key={'mainTicket'}
          >
            <Box sx={{display: 'flex'}}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'normal' }}>
                Main Ticket
              </Typography>
              {!mainTicket?.read && !checkTicketId(id) && <Iconify icon="mdi:circle-medium" width={16} height={16} color="info" />}
            </Box>
          </Link>
          {subtickets?.map((subticket, index) => (
            <EditSubject
              subticket={subticket}
              key={index}
              index={index}
              parentTicketId={id}
              checkTicketId={checkTicketId}
            />
          ))}
        </Stack>
        <Box>
          {!mainTicket?.locked && (
            <LoadingButton
              variant="contained"
              color="info"
              loading={addingSubtcket}
              onClick={handleSend}
              size="small"
              startIcon={
                <Iconify
                  icon="ic:baseline-plus"
                  width={15}
                  height={15}
                  sx={{
                    mr: -1,
                  }}
                />
              }
              sx={{
                cursor: 'pointer',
                backgroundColor: 'info.main',
                paddingX: 0.7,
                paddingY: 0.4,
                ml: 1,
                mb: 0.3,
                fontWeight: 'normal',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'normal' }}>
                Subticket
              </Typography>
            </LoadingButton>
          )}
        </Box>
      </Grid>
    </>
  );
}
